/**
 * PortalPanelContext — global open/tab state for the header "your submissions"
 * panel. SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3.
 *
 * Mounted ONCE above <Router> (App.tsx) so it survives route changes — every
 * page's `DashboardLayout` re-mounts `YourSubmissionsBell` (the panel trigger
 * + shell) per-navigation, but the open/active-tab STATE must be settable from
 * anywhere: `ReportFeedbackButton`'s post-submit receipt flow and
 * `YourIssuesPage`'s portal-enabled redirect both need to OPEN the panel
 * (pre-selected to My Submissions) without importing YourSubmissionsBell
 * directly or depending on it being the same component instance (AC6 — the
 * "land on your receipt via the open panel" requirement).
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type PortalPanelTab = 'my_submissions' | 'roadmap' | 'coordination' | 'all_issues';

export const DEFAULT_PORTAL_PANEL_TAB: PortalPanelTab = 'my_submissions';

interface PortalPanelContextValue {
  isOpen: boolean;
  activeTab: PortalPanelTab;
  /** Open the panel, optionally pre-selecting a tab (defaults to My Submissions). */
  openPanel: (tab?: PortalPanelTab) => void;
  closePanel: () => void;
  /** Wired directly to Popover/Sheet's onOpenChange. */
  setOpen: (open: boolean) => void;
  setActiveTab: (tab: PortalPanelTab) => void;
}

const PortalPanelContext = createContext<PortalPanelContextValue | null>(null);

export function PortalPanelProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PortalPanelTab>(DEFAULT_PORTAL_PANEL_TAB);

  const openPanel = useCallback((tab: PortalPanelTab = DEFAULT_PORTAL_PANEL_TAB) => {
    setActiveTab(tab);
    setIsOpen(true);
  }, []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const setOpen = useCallback((open: boolean) => setIsOpen(open), []);

  const value = useMemo<PortalPanelContextValue>(
    () => ({ isOpen, activeTab, openPanel, closePanel, setOpen, setActiveTab }),
    [isOpen, activeTab, openPanel, closePanel, setOpen],
  );

  return <PortalPanelContext.Provider value={value}>{children}</PortalPanelContext.Provider>;
}

// A safe no-op fallback for the (rare) render tree that mounts DashboardLayout
// -> YourSubmissionsBell / ReportFeedbackButton without the real
// PortalPanelProvider above it — e.g. a pre-existing test harness's own
// bespoke QueryClientProvider+MemoryRouter wrapper that predates this
// context. The panel simply never opens in that tree (isOpen stays false;
// openPanel/setOpen are no-ops) rather than crashing the whole render. The
// REAL app (App.tsx) always mounts PortalPanelProvider above <Router>, so
// production never hits this branch.
const NOOP_PANEL_CONTEXT: PortalPanelContextValue = {
  isOpen: false,
  activeTab: DEFAULT_PORTAL_PANEL_TAB,
  openPanel: () => {},
  closePanel: () => {},
  setOpen: () => {},
  setActiveTab: () => {},
};

export function usePortalPanel(): PortalPanelContextValue {
  const ctx = useContext(PortalPanelContext);
  if (!ctx) {
    if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
      console.warn(
        'usePortalPanel: no PortalPanelProvider found above this component — ' +
        'falling back to a no-op panel (it will never open). Wrap the render ' +
        'tree in <PortalPanelProvider> (App.tsx already does this at the app ' +
        'root; a test harness with its own bespoke QueryClientProvider + ' +
        'MemoryRouter wrapper needs to add it too).',
      );
    }
    return NOOP_PANEL_CONTEXT;
  }
  return ctx;
}
